import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fields = [
  { name: "fullName", label: "Full Name",     placeholder: "Sachin Jangid",   full: true  },
  { name: "phone",    label: "Phone",          placeholder: "+91 98765 43210", full: true  },
  { name: "street",   label: "Street Address", placeholder: "123, MG Road",    full: true  },
  { name: "city",     label: "City",           placeholder: "Delhi",           full: false },
  { name: "state",    label: "State",          placeholder: "Delhi",           full: false },
  { name: "pincode",  label: "Pincode",        placeholder: "110001",          full: false },
];

const emptyAddress = { fullName: "", phone: "", street: "", city: "", state: "", pincode: "" };

const STEPS = ["Cart", "Delivery", "Payment"];

function Checkout({ cart = [] }) {
  const navigate = useNavigate();
  const token    = localStorage.getItem("token");
  const headers  = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedId, setSelectedId]         = useState(null);
  const [mode, setMode]                     = useState("loading");
  const [newAddress, setNewAddress]         = useState(emptyAddress);
  const [saving, setSaving]                 = useState(false);
  const [error, setError]                   = useState("");

  useEffect(() => {
    axios.get(`${API_BASE_URL}/dashboard`, { headers })
      .then(res => {
        const addrs = res.data.addresses || [];
        setSavedAddresses(addrs);
        if (addrs.length > 0) { setSelectedId(addrs[0].id); setMode("select"); }
        else setMode("new");
      })
      .catch(() => setMode("new"));
  }, [headers]);

  const handleChange = (e) => {
    setNewAddress({ ...newAddress, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleContinue = async () => {
    if (mode === "select" && selectedId) { navigate("/payment"); return; }
    const empty = fields.find(f => !newAddress[f.name].trim());
    if (empty) { setError(`${empty.label} is required.`); return; }
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/address`, newAddress, { headers });
      navigate("/payment");
    } catch {
      setError("Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  const originalTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const total         = cart.reduce((sum, item) => sum + (item.salePrice ?? item.price) * item.quantity, 0);
  const discount      = originalTotal - total;
  const hasSale       = discount > 0;

  return (
    <>

      <div className="ck-page">
        {/* Background atmosphere */}
        <div aria-hidden="true" className="ck-bg">
          <div className="ck-orb ck-orb1" />
          <div className="ck-orb ck-orb2" />
          <div className="ck-grid" />
        </div>

        <div className="ck-inner">

          {/* ── Header ── */}
          <div className="ck-header">
            <div>
              <div className="ck-eyebrow"><span className="ck-eyebrow-dot" />Secure Checkout</div>
              <h1 className="ck-title">Delivery</h1>
              <div className="ck-title-divider" />
            </div>

            {/* Step indicator */}
            <div className="ck-steps">
              {STEPS.map((step, i) => (
                <div key={step} className="ck-step-wrap">
                  <div className={`ck-step${i === 1 ? " active" : i < 1 ? " done" : ""}`}>
                    <div className="ck-step-dot">
                      {i < 1
                        ? <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20,6 9,17 4,12" /></svg>
                        : <span>{i + 1}</span>
                      }
                    </div>
                    <span className="ck-step-label">{step}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className="ck-step-line" />}
                </div>
              ))}
            </div>
          </div>

          {/* ── Loading ── */}
          {mode === "loading" && (
            <div className="ck-loading">
              <div className="tc-spinner" />
              <span>Loading your addresses…</span>
            </div>
          )}

          {/* ── Main layout ── */}
          {mode !== "loading" && (
            <div className="ck-body">

              {/* LEFT: Address panel */}
              <div className="ck-left">

                {/* Saved addresses */}
                {savedAddresses.length > 0 && (
                  <div className="ck-card">
                    <div className="ck-card-head">
                      <div className="ck-card-label">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round">
                          <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                        Saved Addresses
                      </div>
                      {mode !== "new" && (
                        <button className="ck-text-btn" onClick={() => { setMode("new"); setSelectedId(null); }}>
                          + New address
                        </button>
                      )}
                    </div>

                    <div className="ck-addr-list">
                      {savedAddresses.map(a => (
                        <button
                          key={a.id}
                          className={"ck-addr-card" + (selectedId === a.id ? " selected" : "")}
                          onClick={() => { setSelectedId(a.id); setMode("select"); setError(""); }}
                        >
                          <div className="ck-addr-radio">
                            <div className={"ck-radio" + (selectedId === a.id ? " active" : "")}>
                              {selectedId === a.id && <div className="ck-radio-dot" />}
                            </div>
                            <span className="ck-addr-name">{a.fullName}</span>
                          </div>
                          <div className="ck-addr-detail">
                            <span>{a.street}</span>
                            <span>{a.city}, {a.state} — {a.pincode}</span>
                            {a.phone && <span>{a.phone}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* New address form */}
                {mode === "new" && (
                  <div className="ck-card">
                    <div className="ck-card-head">
                      <div className="ck-card-label">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round">
                          <path d="M12 5v14M5 12h14"/>
                        </svg>
                        New Delivery Address
                      </div>
                      {savedAddresses.length > 0 && (
                        <button className="ck-text-btn" onClick={() => { setMode("select"); setSelectedId(savedAddresses[0].id); setError(""); }}>
                          ← Use saved
                        </button>
                      )}
                    </div>

                    <div className="ck-field-grid">
                      {fields.map(f => (
                        <div key={f.name} className={"ck-field" + (f.full ? " full" : "")}>
                          <label className="ck-field-label">{f.label}</label>
                          <input
                            className="ck-input"
                            name={f.name}
                            placeholder={f.placeholder}
                            value={newAddress[f.name]}
                            onChange={handleChange}
                            autoComplete="off"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error */}
                {error && <div className="ck-error">{error}</div>}

                {/* Actions */}
                <div className="ck-actions">
                  <button className="ck-btn-primary" onClick={handleContinue} disabled={saving}>
                    {saving
                      ? <><div className="ck-btn-spinner" />Saving…</>
                      : <>Continue to Payment
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </>
                    }
                  </button>
                  <button className="ck-btn-ghost" onClick={() => navigate("/cart")}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    Back to Cart
                  </button>
                </div>
              </div>

              {/* RIGHT: Order summary */}
              {cart.length > 0 && (
                <div className="ck-right">
                  <div className="ck-card ck-summary-card">
                    <div className="ck-card-head">
                      <div className="ck-card-label">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round">
                          <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/>
                        </svg>
                        Order Summary
                      </div>
                      <span className="ck-item-count">{cart.length} item{cart.length !== 1 ? "s" : ""}</span>
                    </div>

                    <div className="ck-items">
                      {cart.map(item => (
                        <div key={item.id} className="ck-item">
                          {item.imageUrl && (
                            <div className="ck-item-img">
                              <img src={item.imageUrl} alt={item.name} />
                            </div>
                          )}
                          <div className="ck-item-info">
                            <span className="ck-item-name">{item.name}</span>
                            <span className="ck-item-qty">Qty {item.quantity}</span>
                          </div>
                          <div className="ck-item-price-block">
                            {item.salePrice != null && (
                              <span className="ck-item-price-was">
                                ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                              </span>
                            )}
                            <span className="ck-item-price">
                              ₹{((item.salePrice ?? item.price) * item.quantity).toLocaleString("en-IN")}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="ck-summary-divider" />

                    {hasSale && (
                      <div className="ck-summary-row">
                        <span className="ck-summary-label">Original Price</span>
                        <span className="ck-summary-val ck-summary-val-strike">₹{originalTotal.toLocaleString("en-IN")}</span>
                      </div>
                    )}
                    {hasSale && (
                      <div className="ck-summary-row">
                        <span className="ck-summary-label ck-summary-label-discount">Discount Saved</span>
                        <span className="ck-summary-val ck-summary-val-discount">− ₹{discount.toLocaleString("en-IN")}</span>
                      </div>
                    )}
                    <div className="ck-summary-row">
                      <span className="ck-summary-label">Subtotal</span>
                      <span className="ck-summary-val">₹{total.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="ck-summary-row">
                      <span className="ck-summary-label">Shipping</span>
                      <span className="ck-free-badge">FREE</span>
                    </div>

                    <div className="ck-summary-divider" />

                    {hasSale && (
                      <div className="ck-savings-banner">
                        You're saving ₹{discount.toLocaleString("en-IN")} on this order
                      </div>
                    )}
                    <div className="ck-total-row">
                      <span className="ck-total-label">Total</span>
                      <div className="ck-total-price">
                        <span className="ck-total-currency">₹</span>
                        <span className="ck-total-num">{total.toLocaleString("en-IN")}</span>
                      </div>
                    </div>

                    {/* Trust signals */}
                    <div className="ck-trust">
                      <div className="ck-trust-item">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </svg>
                        Secure checkout
                      </div>
                      <div className="ck-trust-item">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round">
                          <polyline points="20,6 9,17 4,12"/>
                        </svg>
                        7-day returns
                      </div>
                      <div className="ck-trust-item">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round">
                          <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                        </svg>
                        Fast delivery
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Checkout;

